import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { friendlyDateTime, friendlyDateTimeRange, friendlyDay } from '../../utils/ai-date.util';
import { AiFriendlyDatePipe } from '../../pipes/ai-friendly-date.pipe';
import {
  AiAppointmentItem,
  AiAppointmentListView,
  AiBriefingItem,
  AiCapabilitiesView,
  AiContactListView,
  AiDayBriefingView,
  AiFollowUpsAddedView,
  AiFollowUpsCompletedView,
  AiFollowUpsMovedView,
  AiMyAppointmentListView,
  AiLeadCreatedView,
  AiLeadsClosedView,
  AiLeadsUpdatedView,
  AiLeadDetailView,
  AiLeadGridColumn,
  AiLeadGridView,
  AiLeadListView,
  AiLeadStatsView,
  AiLeadViewsView,
  AiLeadViewSavedView,
  AiOpenLeadRequest,
  AiProjectView,
  AiStatBucket,
  AiView,
  AiViewContactItem,
  AiViewLeadItem,
} from '../../models/ai-assistant.models';

/** Normalized shape for the family of write-confirmation cards (update / follow-up / etc.). */
interface AiConfirmCard {
  icon: string;
  title: string;
  name?: string;
  meta: string[];
  leadId: string;
  /** The lead's project (Round 5) — opening the lead jumps to its project when set. */
  projectId?: string | null;
}

/** A normalized project/lot/EOI/task/event row: a primary label + a few best-effort detail chips. */
interface AiProjectRow {
  title: string;
  meta: string[];
}

/** A label/value pair for the project-detail card. */
interface AiKeyVal {
  label: string;
  value: string;
}

/**
 * Renders the structured `views[]` channel returned by `POST /chat` and `POST /confirm`
 * (FRONTEND_HANDOFF §0). Each view is a discriminated union on `type`; unknown future
 * types are ignored so adding a backend module is render-only here.
 *
 * Presentational: emits intent up (open a lead, send a follow-up prompt for pagination /
 * suggested actions, switch a saved view). The panel owns navigation + sending.
 */
@Component({
  selector: 'app-ai-assistant-views',
  standalone: true,
  imports: [CommonModule, AiFriendlyDatePipe],
  templateUrl: './ai-assistant-views.component.html',
  styleUrls: ['./ai-assistant-views.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantViewsComponent {
  constructor(private readonly toastr: ToastrService) {}

  @Input() views: AiView[] | null = null;

  /**
   * Open a lead from a card. Carries the lead id plus its `project_id` (Round 5): when the
   * lead has a project the panel opens the project, else the lead. Empty id falls back to the list.
   */
  @Output() openLead = new EventEmitter<AiOpenLeadRequest>();
  /** Open a lead record straight on its Appointment tab (for appointment rows). */
  @Output() openLeadOnAppointments = new EventEmitter<string>();
  /** Send a follow-up chat turn (pagination "show more", a suggested prompt, a saved-view switch). */
  @Output() pickPrompt = new EventEmitter<string>();

  /**
   * Emit an open-lead request from a card. `projectId` (Round 5) rides along so the panel can
   * open the lead's project; a blank lead id is ignored (nothing to open).
   */
  emitOpenLead(leadId?: string | null, projectId?: string | null): void {
    const id = `${leadId ?? ''}`.trim();
    if (!id) {
      return;
    }
    this.openLead.emit({ leadId: id, projectId: `${projectId ?? ''}`.trim() || null });
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(_index: number, item: { id?: string }): string {
    return item?.id || String(_index);
  }

  // ---- Typed narrowing helpers (template can't use `as`) ----------------------

  asLeadList(v: AiView): AiLeadListView {
    return v as AiLeadListView;
  }
  asLeadDetail(v: AiView): AiLeadDetailView {
    return v as AiLeadDetailView;
  }
  asLeadCreated(v: AiView): AiLeadCreatedView {
    return v as AiLeadCreatedView;
  }
  asLeadStats(v: AiView): AiLeadStatsView {
    return v as AiLeadStatsView;
  }
  asContactList(v: AiView): AiContactListView {
    return v as AiContactListView;
  }
  asLeadGrid(v: AiView): AiLeadGridView {
    return v as AiLeadGridView;
  }
  asLeadViews(v: AiView): AiLeadViewsView {
    return v as AiLeadViewsView;
  }
  asLeadViewSaved(v: AiView): AiLeadViewSavedView {
    return v as AiLeadViewSavedView;
  }
  asCapabilities(v: AiView): AiCapabilitiesView {
    return v as AiCapabilitiesView;
  }

  asAppointmentList(v: AiView): AiAppointmentListView {
    return v as AiAppointmentListView;
  }

  asMyAppointmentList(v: AiView): AiMyAppointmentListView {
    return v as AiMyAppointmentListView;
  }

  asFollowUpsAdded(v: AiView): AiFollowUpsAddedView {
    return v as AiFollowUpsAddedView;
  }

  /** Lead id for a my-appointment row — a LEAD reference only (never the appointment `id`). */
  myAppointmentLeadId(item: AiAppointmentItem): string {
    return this.leadRef(item);
  }

  /**
   * Open the lead behind an appointment/inspection row. The backend doesn't always link an
   * appointment to a lead (`lead_id` absent) — rather than a silent no-op that looks like a
   * broken click, tell the user why nothing opened.
   */
  openAppointmentLead(leadId: string): void {
    if (!leadId) {
      this.toastr.info("This appointment isn't linked to a lead yet.");
      return;
    }
    this.openLeadOnAppointments.emit(leadId);
  }

  /**
   * Pull a LEAD reference off an appointment/briefing row, tolerating the shapes the backend
   * may use (`lead_id`, `leadId`, or a nested `lead` object/string). Deliberately never falls
   * back to the row's own `id` — that's the appointment id, and navigating with it opens no lead.
   */
  private leadRef(item: any): string {
    const raw = item?.lead_id ?? item?.leadId ?? item?.lead?._id ?? item?.lead?.id ?? (typeof item?.lead === 'string' ? item.lead : '');
    return `${raw ?? ''}`.trim();
  }

  asDayBriefing(v: AiView): AiDayBriefingView {
    return v as AiDayBriefingView;
  }

  asFollowUpsMoved(v: AiView): AiFollowUpsMovedView {
    return v as AiFollowUpsMovedView;
  }

  asLeadsClosed(v: AiView): AiLeadsClosedView {
    return v as AiLeadsClosedView;
  }

  asFollowUpsCompleted(v: AiView): AiFollowUpsCompletedView {
    return v as AiFollowUpsCompletedView;
  }

  asLeadsUpdated(v: AiView): AiLeadsUpdatedView {
    return v as AiLeadsUpdatedView;
  }

  /** The applied changes as friendly "Field → value" chips, e.g. "Lead Status → Contact Attempted". */
  updateChanges(view: AiLeadsUpdatedView): string[] {
    const changes = view?.changes || {};
    return Object.keys(changes)
      .map(key => {
        const value = `${changes[key] ?? ''}`.trim();
        return value ? `${this.humanizeField(key)} → ${value}` : '';
      })
      .filter(Boolean);
  }

  asProject(v: AiView): AiProjectView {
    return v as AiProjectView;
  }

  /** Display name for a bulk-result row (never an id). */
  bulkItemName(item: AiBriefingItem): string {
    return item?.name || 'Lead';
  }

  // ---- Project views (defensive — exact item fields are backend-driven) -------

  /** Candidate keys for a row's primary label, in priority order. */
  private static readonly PROJ_TITLE_KEYS = [
    'name',
    'title',
    'full_name',
    'project_name',
    'lot_name',
    'lot_number',
    'lot_no',
    'label',
    'event_title',
    'task_title',
    'subject',
  ];

  /** Keys surfaced as detail chips on a project row, in priority order (max 4 shown). */
  private static readonly PROJ_META_KEYS = [
    'status',
    'lead_status',
    'lot_status',
    'eoi_status',
    'stage',
    'type',
    'event_type',
    'task_type',
    'price',
    'amount',
    'address',
    'suburb',
    'date',
    'due_date',
    'start_time',
    'end_time',
    'assigned_agent',
    'agent',
    'total_lots',
    'total_eois',
  ];

  /** Internal / id-ish keys never displayed. */
  private static readonly PROJ_HIDDEN = /(^_?id$)|(_id$)|created_at|updated_at|office_id|conversation_id|__v/i;
  private static readonly PROJ_DATE_KEYS = new Set(['date', 'due_date', 'event_date', 'start_date', 'end_date']);

  /** Heading for a project view, scoped by the project name when present. */
  projectHeading(view: AiProjectView): string {
    const name = (view?.project_name || '').trim();
    switch (view?.type) {
      case 'project_list':
        return 'Projects';
      case 'project_detail':
        return name || 'Project';
      case 'lot_list':
        return name ? `${name} · Lots` : 'Lots';
      case 'eoi_list':
        return name ? `${name} · EOIs` : 'EOIs';
      case 'project_task_list':
        return name ? `${name} · Tasks` : 'Project tasks';
      case 'project_event_list':
        return name ? `${name} · Events` : 'Project events';
      default:
        return name || 'Projects';
    }
  }

  /** PrimeIcons glyph for a project view type. */
  projectIcon(view: AiProjectView): string {
    switch (view?.type) {
      case 'lot_list':
        return 'pi-map';
      case 'eoi_list':
        return 'pi-file-edit';
      case 'project_task_list':
        return 'pi-check-square';
      case 'project_event_list':
        return 'pi-calendar';
      default:
        return 'pi-building';
    }
  }

  /** Normalize the list rows into `{ title, meta[] }` (ids hidden). */
  projectItems(view: AiProjectView): AiProjectRow[] {
    return (view?.items || []).map(it => ({ title: this.projTitle(it), meta: this.projMeta(it) }));
  }

  /** The single record for a `project_detail` view (falls back to `items[0]`). */
  private projectDetailItem(view: AiProjectView): Record<string, any> | null {
    return view?.item || view?.items?.[0] || null;
  }

  /** Label/value rows for a `project_detail` card (ids hidden, dates friendly). */
  projectDetailRows(view: AiProjectView): AiKeyVal[] {
    const item = this.projectDetailItem(view) || {};
    return Object.keys(item)
      .filter(k => !AiAssistantViewsComponent.PROJ_HIDDEN.test(k))
      .map(k => ({ label: this.humanizeKey(k), value: this.projScalar(item[k], k) }))
      .filter(r => r.value !== '');
  }

  /** A lot/EOI list has more pages to fetch. */
  projectHasMore(view: AiProjectView): boolean {
    const total = Number(view?.total ?? 0);
    const page = Number(view?.page ?? 1);
    const perPage = Number(view?.per_page ?? 0);
    return total > 0 && perPage > 0 && page * perPage < total;
  }

  private projTitle(item: Record<string, any>): string {
    for (const k of AiAssistantViewsComponent.PROJ_TITLE_KEYS) {
      const v = item?.[k];
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
      if (typeof v === 'number') {
        return `${v}`;
      }
    }
    for (const k of Object.keys(item || {})) {
      if (AiAssistantViewsComponent.PROJ_HIDDEN.test(k)) {
        continue;
      }
      const v = item[k];
      if ((typeof v === 'string' && v.trim()) || typeof v === 'number') {
        return `${v}`;
      }
    }
    return 'Item';
  }

  private projMeta(item: Record<string, any>): string[] {
    const out: string[] = [];
    for (const k of AiAssistantViewsComponent.PROJ_META_KEYS) {
      if (!(item && k in item)) {
        continue;
      }
      const v = this.projScalar(item[k], k);
      if (v) {
        out.push(v);
      }
      if (out.length >= 4) {
        break;
      }
    }
    return out;
  }

  /** Collapse a value to a readable string, unwrapping objects and formatting dates. */
  private projScalar(value: unknown, key?: string): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      return value
        .map(v => this.projScalar(v))
        .filter(Boolean)
        .join(', ');
    }
    if (typeof value === 'object') {
      const o = value as Record<string, unknown>;
      return `${o['name'] ?? o['label'] ?? o['title'] ?? o['value'] ?? ''}`;
    }
    if (typeof value === 'string' && key && AiAssistantViewsComponent.PROJ_DATE_KEYS.has(key) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return friendlyDay(value);
    }
    return `${value}`.trim();
  }

  private humanizeKey(key: string): string {
    const text = (key || '').replace(/_/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // ---- Day briefing / follow-ups-moved helpers --------------------------------

  /** Open-lead id for a briefing row (lead_id preferred, never rendered). */
  briefingItemId(item: AiBriefingItem): string {
    return item?.lead_id || item?.id || '';
  }

  /** Friendly follow-up time for a briefing row, e.g. "Today, 3:00 PM". */
  briefingItemWhen(item: AiBriefingItem): string {
    return friendlyDateTime(item?.follow_up_date, item?.follow_up_time || item?.task_time || item?.time);
  }

  /** When for a day-briefing appointment row — prefers `start_time`, else the follow-up time. */
  appointmentItemWhen(item: AiBriefingItem): string {
    return (item?.start_time || '').trim() || this.briefingItemWhen(item);
  }

  /**
   * Lead id for an appointment row — ONLY `lead_id`. Never fall back to the row's own `id`
   * (that's the appointment id, not a lead) — navigating with it opens no lead and triggers a
   * "not found" reload storm. Empty → the row isn't clickable.
   */
  appointmentLeadId(item: AiBriefingItem): string {
    return this.leadRef(item);
  }

  /** Lead status for a briefing row — backend sends `status` or `lead_status`. */
  briefingStatus(item: AiBriefingItem): string {
    return (item?.status || item?.lead_status || '').trim();
  }

  /** Lead type for a briefing row — backend sends `type` or `lead_type`. */
  briefingType(item: AiBriefingItem): string {
    return (item?.type || item?.lead_type || '').trim();
  }

  /** Friendly target for a bulk follow-up move, e.g. "Today, 2:00 PM". */
  movedWhen(view: AiFollowUpsMovedView): string {
    return friendlyDateTime(view?.to_date, view?.to_time);
  }

  /** Friendly due date/time for a bulk follow-up add, e.g. "Tomorrow, 9:00 AM". */
  addedWhen(view: AiFollowUpsAddedView): string {
    return friendlyDateTime(view?.due_date, view?.task_time);
  }

  /**
   * Collapse the write-confirmation views (`lead_updated`, `follow_up_saved`,
   * `follow_up_completed`, `contact_logged`, `appointment_booked`) into one card model so
   * they share a single template. Each shows an icon, title, lead name, optional detail
   * chips, and an Open Lead action.
   */
  confirmCard(view: AiView): AiConfirmCard {
    const v = view as any;
    const item = v.item || {};
    const leadId: string = v.actions?.[0]?.lead_id || item.lead_id || item.id || '';
    const projectId: string | null = item.project_id ?? null;
    const dateTime = friendlyDateTime(item.date, item.time || item.task_time);

    const base = ((): Omit<AiConfirmCard, 'leadId' | 'projectId'> => {
      switch (view.type) {
        case 'lead_updated':
          return {
            icon: 'pi-check-circle',
            title: 'Lead updated',
            name: item.name,
            meta: (item.updated_fields || []).map((f: string) => this.humanizeField(f)),
          };
        case 'follow_up_saved':
          return {
            icon: 'pi-bell',
            title: 'Follow-up scheduled',
            name: item.name,
            meta: [friendlyDateTime(item.due_date, item.task_time)].filter(Boolean),
          };
        case 'follow_up_completed':
          return { icon: 'pi-check-circle', title: 'Follow-up completed', name: item.name, meta: [] };
        case 'contact_logged':
          return { icon: 'pi-phone', title: 'Contact logged', name: item.name, meta: item.channels || [] };
        case 'appointment_booked':
          return { icon: 'pi-calendar', title: 'Appointment booked', name: item.name, meta: [item.title, dateTime].filter(Boolean) };
        default:
          return { icon: 'pi-check-circle', title: 'Done', name: item.name, meta: [] };
      }
    })();
    return { ...base, leadId, projectId };
  }

  /** Date + time line for an appointment row, e.g. "Thu 19 Jun, 9:00 AM – 9:30 AM". */
  appointmentWhen(a: AiAppointmentItem): string {
    return friendlyDateTimeRange(a.date, a.start_time, a.end_time);
  }

  // ---- Lead / contact card helpers -------------------------------------------

  initials(name?: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  /** Subtitle under the lead name — the lead status (e.g. "Nurturing (Pre Appointment)"). */
  leadSubtitle(lead: AiViewLeadItem): string {
    return lead?.lead_status || '';
  }

  temperatureClass(lead: AiViewLeadItem): string {
    const temp = (lead?.temperature || '').toLowerCase();
    if (temp === 'hot') return 'ai-lead-card__temp--hot';
    if (temp === 'warm') return 'ai-lead-card__temp--warm';
    if (temp === 'cold') return 'ai-lead-card__temp--cold';
    return '';
  }

  telHref(phone?: string): string {
    return 'tel:' + (phone || '').replace(/\s+/g, '');
  }

  mailtoHref(email?: string): string {
    return 'mailto:' + (email || '').trim();
  }

  /** Human label for a field key, e.g. `email` → `Email`, `full_name` → `Full name`. */
  humanizeField(field: string): string {
    const text = (field || '').replace(/_/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // ---- Stats helpers ---------------------------------------------------------

  /** Largest bucket count in a series, for proportional bar widths (min 1 to avoid /0). */
  maxCount(buckets?: AiStatBucket[]): number {
    return Math.max(1, ...(buckets || []).map(b => b.count || 0));
  }

  barWidth(count: number, buckets?: AiStatBucket[]): string {
    const pct = Math.round((Math.max(0, count) / this.maxCount(buckets)) * 100);
    return Math.max(4, pct) + '%';
  }

  // ---- Grid helpers ----------------------------------------------------------

  /**
   * Internal/technical columns must never reach the user — ids, foreign keys, and raw
   * timestamps. Matches `id`/`_id`, anything ending in `_id`, and created/updated/conversation/
   * office keys.
   */
  private static readonly HIDDEN_GRID_FIELDS = /^_?id$|_id$|^created_at$|^updated_at$|^conversation_id$|^office_id$/i;

  isHiddenField(field?: string): boolean {
    return AiAssistantViewsComponent.HIDDEN_GRID_FIELDS.test((field || '').trim());
  }

  /** Grid columns with internal/technical fields stripped out (see {@link isHiddenField}). */
  visibleColumns(view: AiLeadGridView): AiLeadGridColumn[] {
    return (view?.columns || []).filter(col => !this.isHiddenField(col?.field));
  }

  /** Render a grid cell as a readable string (objects/arrays collapse to a dash). */
  cellText(row: Record<string, any>, field: string): string {
    if (this.isHiddenField(field)) {
      return '—';
    }
    const value = row?.[field];
    if (value == null || value === '') {
      return '—';
    }
    if (typeof value === 'object') {
      // Common shapes: { name } / { full_name } / array of names.
      if (Array.isArray(value)) {
        return (
          value
            .map(v => (typeof v === 'object' ? v?.name || v?.full_name || '' : v))
            .filter(Boolean)
            .join(', ') || '—'
        );
      }
      return value.name || value.full_name || value.label || '—';
    }
    return String(value);
  }

  contactCompany(c: AiViewContactItem): string {
    return c?.company || '';
  }
}
